import { PdfGen, PdfUnit, PdfSize, PdfRect, PdfBrush, ETextAlign } from '../../pdf/pdf';
import { ILayoutListGen } from '../layoutList';
import { OptsList, getListItems } from '../optsList';
import { GUtil } from '../../pdf/gutil';
import { calcOrigin } from '../../core/genHelper';
import { IItemCount } from '@educraft/base';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';

/**
 * SpojovackaOrder (Matching/Connection Exercise for List Layout)
 *
 * Layout: Two columns with circles for drawing connection lines.
 * Left column: values (shuffled), Right column: keys (in order).
 *
 * Migrated from C# EduFrame.Order.Generators.Spojovacka
 */
export class SpojovackaOrder implements ILayoutListGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsList): Promise<void> {
    const margin = PdfUnit.FromMillimeters(20);
    const margin2 = PdfUnit.FromMillimeters(5);

    const items = getListItems(opts);
    if (items.length === 0) return;

    const itemsPerPage = 15;
    const rnd = opts.data.random;

    for (let i = 0; i < items.length; i += itemsPerPage) {
      const page = pdf.addPage();
      const currentPageItems = items.slice(i, i + itemsPerPage);
      const count = currentPageItems.length;

      const { origin, size } = calcOrigin(3, count, margin, PdfSize.A4);

      // Adjust fonts to fit current page items
      const f1 = page.adjustFont(
        size.width,
        currentPageItems.map((it) => it.value),
        GUtil.baseFont
      );
      const f2 = page.adjustFont(
        size.width,
        currentPageItems.map((it) => it.key),
        GUtil.baseFont
      );

      // Measure max widths for alignment
      const m1 = Math.max(...currentPageItems.map((it) => page.measureString(it.value, f1).width));
      const m2 = Math.max(...currentPageItems.map((it) => page.measureString(it.key, f2).width));

      // Shuffle left side (values) for each page
      const shuffled = [...currentPageItems];
      for (let p = shuffled.length - 1; p > 0; p--) {
        const j = rnd.next(0, p + 1);
        [shuffled[p], shuffled[j]] = [shuffled[j], shuffled[p]];
      }

      // Draw rows
      for (let j = 0; j < count; j++) {
        const r1 = new PdfRect(origin.x, origin.y + j * size.height, m1, size.height);
        const r2 = new PdfRect(
          origin.x + 3 * size.width - m2,
          origin.y + j * size.height,
          m2,
          size.height
        );

        // Connection circles
        const c1 = new PdfRect(
          r1.right + margin2,
          r1.centerY - GUtil.circleHandleSize / 2,
          GUtil.circleHandleSize,
          GUtil.circleHandleSize
        );
        const c2 = new PdfRect(
          r2.left - margin2 - GUtil.circleHandleSize,
          r1.centerY - GUtil.circleHandleSize / 2,
          GUtil.circleHandleSize,
          GUtil.circleHandleSize
        );

        // Draw shuffled value on left
        page.drawString(shuffled[j].value, f1, PdfBrush.Black, null, r1, ETextAlign.CenterRight);
        // Draw ordered key on right
        page.drawString(
          currentPageItems[j].key,
          f2,
          PdfBrush.Black,
          null,
          r2,
          ETextAlign.CenterLeft
        );

        page.drawEllipse(GUtil.basePen, null, c1);
        page.drawEllipse(GUtil.basePen, null, c2);
      }
    }
  }
}
